import React from 'react';
import {
    Box,
    Divider,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Collapse,
    IconButton, CardContent, Card,
} from '@mui/material';
import {InputField} from "@/components/common";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {SxProps} from "@mui/system";
import {Theme} from "@mui/material/styles/createTheme";

interface HeaderFieldProps {
    label: string;
    value?: string;
    sx?: SxProps<Theme>;
}

export interface C7PerformanceValues {
    initial?: string;
    final?: string;
}

export interface C7MaterialitySummaryProps {
    client?: string;
    yearEnd?: string;
    preparedBy?: string;
    reviewedBy?: string;
    overallInitial?: string;
    overallFinal?: string;
    performanceBySection?: Record<string, C7PerformanceValues>;
}

const HeaderField: React.FC<HeaderFieldProps> = ({label, value, sx}) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={sx}>
        <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap',minWidth:'58px'}}>
            {label}
        </Typography>
        <Box sx={{flex: 1}}>
            <InputField
                type="text"
                fullWidth
                compact
                align="left"
                defaultValue={value}
            />
        </Box>
    </Stack>
);

const C7MaterialitySummary: React.FC<C7MaterialitySummaryProps> = ({
    client,
    yearEnd,
    preparedBy,
    reviewedBy,
    overallInitial,
    overallFinal,
    performanceBySection,
}) => {
    const perf = (key: string) => performanceBySection?.[key];
    const [incomeExpanded, setIncomeExpanded] = React.useState<boolean>(false);
    return (
        <Box p={2}>
            {/* Header section */}
            <Card>
                <CardContent>
                    <Grid container spacing={2} alignItems="flex-start">
                        {/* Column 1: Client, Year end, File no */}
                        <Grid size={{xs: 12, md: 3}}>
                            <HeaderField label="Client:" value={client}/>
                            <HeaderField label="Year end:" value={yearEnd}/>
                            <HeaderField label="File no:"/>
                        </Grid>
                        {/* Column 2: Prepared by, Reviewed by */}
                        <Grid size={{xs: 12, md: 3}}>
                            <HeaderField label="Prepared by:" value={preparedBy}/>
                            <HeaderField label="Reviewed by:" value={reviewedBy}/>
                        </Grid>
                        {/* Column 3: Date, Date */}
                        <Grid size={{xs: 2, md: 3}}>
                            <HeaderField label="Date:"/>
                            <HeaderField label="Date:"/>
                        </Grid>
                        {/* Column 4: Ref */}
                        <Grid size={{xs: 3, md: 3}}>
                            <HeaderField label="Ref:" value="C7" sx={{whiteSpace: 'nowrap',minWidth: '50%'}}/>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>


            <Divider sx={{my: 2}}/>

            {/* Overall materiality */}
            <Box p={2}>
                {/* Header section */}
                <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                        <Typography variant="h5" sx={{fontWeight: 600}}>
                            MATERIALITY SUMMARY
                        </Typography>
                    </Grid>
                    <Grid size={{md: 6}}>
                        <Typography variant="body1">Overall materiality is set at:</Typography>
                    </Grid>
                    <Grid spacing={2} size={{xs: 12, md: 3}}>
                        <HeaderField label="Initial:" value={overallInitial}/>

                    </Grid>
                    <Grid spacing={2} size={{xs: 12, md: 3}}>
                        <HeaderField label="Final:" value={overallFinal}/>
                    </Grid>
                </Grid>
            </Box>


            <Box mt={2}>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell rowSpan={2}/>
                                <TableCell colSpan={2} rowSpan={2}/>
                                <TableCell align="center" rowSpan={2}>
                                    Section specific materiality
                                </TableCell>
                                <TableCell align="center" rowSpan={2}>Justification for section specific materiality</TableCell>
                                <TableCell align="center" rowSpan={2}>Risk factor for section (C8.4)</TableCell>
                                <TableCell align="center" colSpan={2}>
                                    Performance materiality
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{width: 120}} align="center">
                                    Initial
                                </TableCell>
                                <TableCell sx={{width: 120}} align="center">
                                    Final
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* E to M */}
                            {[
                                {code: 'E', section: 'Intangible assets'},
                                {code: 'F', section: 'Property, plant and equipment'},
                                {code: 'G', section: 'Investments in group & associated undertakings'},
                                {code: 'H', section: 'Other investments'},
                                {code: 'I', section: 'Inventories'},
                                {code: 'J', section: 'Trade and other receivables'},
                                {code: 'K', section: 'Bank and cash'},
                                {code: 'L', section: 'Trade and other payables'},
                                {code: 'M', section: 'Long-term loans and deferred income'},
                            ].map((r) => {
                                const sectionPerf = perf(r.code);
                                return (
                                <TableRow key={r.code}>
                                    <TableCell/>
                                    <TableCell>{r.code}</TableCell>
                                    <TableCell sx={{width: 260}}>{r.section}</TableCell>
                                    <TableCell align="center">
                                        <InputField type="text" fullWidth compact align="center" placeholder="None"
                                                    defaultValue="None"/>
                                    </TableCell>
                                    <TableCell align="center">
                                        <InputField type="text" fullWidth compact align="center" placeholder="None"
                                                    defaultValue="None"/>
                                    </TableCell>
                                    <TableCell align="center">
                                        <InputField type="text" compact align="center" placeholder="1.2"
                                                    defaultValue={1.2}/>
                                    </TableCell>
                                    <TableCell align="center">
                                        <InputField type="text" compact align="center" placeholder="0"
                                                    defaultValue={sectionPerf?.initial ?? 0}/>
                                    </TableCell>
                                    <TableCell align="center">
                                        <InputField type="text" compact align="center" placeholder="0"
                                                    defaultValue={sectionPerf?.final ?? 0}/>
                                    </TableCell>
                                </TableRow>
                            )})}

                            {/* R - Income statement header with collapse toggle */}
                            <TableRow>
                                <TableCell sx={{width: 56, p: 0}}>
                                    <IconButton size="small" onClick={() => setIncomeExpanded(v => !v)}
                                                aria-label="expand row">
                                        {incomeExpanded ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                                    </IconButton>
                                </TableCell>
                                <TableCell>R</TableCell>
                                <TableCell colSpan={6}>Income statement</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={8}>
                                    <Collapse in={incomeExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{m: 1}}>
                                            <Table size="small">
                                                <TableBody>
                                                    {[
                                                        {key: 'R-income', section: '- Income', risk: 1.6, initial: 0.08},
                                                        {key: 'R-expenses', section: '- expenses', risk: 1.2, initial: 0},
                                                        {key: 'R-directors', section: "- directors' remuneration", risk: 1.2, initial: 0},
                                                        {key: 'R-related', section: '- related party transactions', risk: 1.2, initial: 0},
                                                        {key: 'R-other', section: 'Other (specify)', risk: undefined, initial: undefined},
                                                    ].map((r, idx) => {
                                                        const sectionPerf = r.key !== 'R-other' ? perf(r.key) : undefined;
                                                        return (
                                                        <TableRow key={`R-${idx}`}>
                                                            <TableCell sx={{width: 56}}/>
                                                            <TableCell/>
                                                            <TableCell>{r.section}</TableCell>
                                                            <TableCell align="center">
                                                                <InputField type="text" fullWidth compact align="center"
                                                                            placeholder="None"
                                                                            defaultValue={idx === 4 ? '' : 'None'}/>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <InputField type="text" fullWidth compact align="center"
                                                                            placeholder="None"
                                                                            defaultValue={idx === 4 ? '' : 'None'}/>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <InputField type="text" compact align="center"
                                                                            placeholder={r.risk === undefined ? '' : String(r.risk)}
                                                                            defaultValue={r.risk} sx={{maxWidth: 120}}/>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <InputField type="text" compact align="center"
                                                                            placeholder={r.initial === undefined ? '' : String(r.initial)}
                                                                            defaultValue={sectionPerf?.initial ?? r.initial}
                                                                            sx={{maxWidth: 120}}/>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <InputField type="text" compact align="center"
                                                                            placeholder="0"
                                                                            defaultValue={sectionPerf?.final ?? ''}
                                                                            sx={{maxWidth: 120}}/>
                                                            </TableCell>
                                                        </TableRow>
                                                    )})}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </Collapse>
                                </TableCell>
                            </TableRow>

                            {/* W row */}
                            <TableRow>
                                <TableCell/>
                                <TableCell>W</TableCell>
                                <TableCell>Group components</TableCell>
                                <TableCell align="center">
                                    <InputField type="text" fullWidth compact align="center" placeholder=""/>
                                </TableCell>
                                <TableCell align="center">Where applicable see C7.2</TableCell>
                                <TableCell align="center">
                                    <InputField type="text" compact align="center" sx={{maxWidth: 120}}/>
                                </TableCell>
                                <TableCell align="center">
                                    <InputField type="text" compact align="center" sx={{maxWidth: 120}}/>
                                </TableCell>
                                <TableCell align="center">
                                    <InputField type="text" compact align="center" sx={{maxWidth: 120}}/>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                    Note. Performance materiality for a section is determined by dividing the overall materiality by the
                    risk factor. However, where a lower level of materiality is specified for a section that lower level
                    should be used in the determination of performance materiality for that section.
                </Typography>
            </Box>
        </Box>
    );
};

export default C7MaterialitySummary;
